// HeaderContext.tsx
"use client";

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

export type HeaderAction = {
  label: string | ReactNode;
  onClick?: () => void;
  isActive?: boolean;
  id?: string;
  size?: string;
};

export type ButtonAction = {
  label: string;
  onClick: () => void;
  isActive?: boolean;
  id?: string;
  size?: string;
  fullWidthOnMobile?: boolean;
};

const buttonSize = ["sm", "md", "lg"] as const;

export type HeaderConfig = {
  title?: ReactNode;
  showTitleOnDesktop?: boolean;
  showHomeOptionsOnMobile?: boolean;
  showBack?: boolean;
  actions?: HeaderAction[];
  backAction?: () => void;
  backLabel?: string;
  buttons?: ButtonAction[];
  buttonLayout?: "inline" | "mobile-wrapped";
};

type HeaderContextValue = {
  config: HeaderConfig;
  setConfig: (config: HeaderConfig) => void;
};

const HeaderContext = createContext<HeaderContextValue | null>(null);

export function HeaderProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<HeaderConfig>({});

  return (
    <HeaderContext.Provider value={{ config, setConfig }}>
      {children}
    </HeaderContext.Provider>
  );
}

export function useHeader(config: HeaderConfig) {
  const context = useContext(HeaderContext);

  if (!context) {
    throw new Error("useHeader deve ser usado dentro de HeaderProvider");
  }

  const { setConfig } = context;

  useEffect(() => {
    setConfig(config);
  }, [config, setConfig]);

  useEffect(() => () => setConfig({}), [setConfig]);
}

export function useHeaderConfig() {
  const context = useContext(HeaderContext);

  if (!context) {
    throw new Error("Header fora de HeaderProvider");
  }

  const { buttons, actions } = context.config;

  useEffect(() => {
    buttons?.forEach((button) => {
      if (
        button.size &&
        !buttonSize.includes(button.size as (typeof buttonSize)[number])
      ) {
        throw new Error(
          `Invalid button size: ${button.size}. Change to one of ${buttonSize.join(", ")}.`,
        );
      }
    });

    actions?.forEach((action) => {
      if (
        action.size &&
        !buttonSize.includes(action.size as (typeof buttonSize)[number])
      ) {
        throw new Error(
          `Invalid action size: ${action.size}. Change to one of ${buttonSize.join(", ")}.`,
        );
      }
    });
  }, [buttons, actions]);

  return context.config;
}
